/**
 * Helper para retry automático de requisições API com backoff exponencial
 *
 * Implementa retry para erros temporários (502, 504) com backoff exponencial
 * e logs detalhados de tentativas.
 */

import { api, APIError, type ApiOverrideOptions } from "./api";
import { sendToTerminal } from "./terminalLogger";
import { LogCategory } from "../config/logging";
import { isRetryableError } from "./toolsErrorHandler";

export interface ApiRetryConfig {
	/**
	 * Número máximo de tentativas (incluindo a primeira)
	 * @default 3
	 */
	maxRetries?: number;

	/**
	 * Status codes que devem ser retentados
	 * @default [502, 504]
	 */
	retryableStatuses?: number[];

	/**
	 * Base do backoff exponencial em milissegundos
	 * @default 1000 (1 segundo)
	 */
	backoffBase?: number;

	/**
	 * Multiplicador do backoff exponencial
	 * @default 2
	 */
	backoffMultiplier?: number;

	/**
	 * Callback chamado antes de cada retry
	 */
	onRetry?: (attempt: number, error: APIError, delayMs: number) => void;
}

const DEFAULT_CONFIG: Required<Omit<ApiRetryConfig, "onRetry">> = {
	maxRetries: 3,
	retryableStatuses: [502, 504],
	backoffBase: 1000,
	backoffMultiplier: 2,
};

/**
 * Calcula o delay para o próximo retry usando backoff exponencial
 */
function calculateBackoffDelay(
	attempt: number,
	base: number,
	multiplier: number,
): number {
	return base * Math.pow(multiplier, attempt);
}

/**
 * Executa uma requisição API com retry automático para erros temporários
 *
 * @param endpoint - Endpoint da API (relativo, ex: '/tools/...')
 * @param options - Opções da requisição (RequestInit)
 * @param config - Configuração de retry
 * @returns Promise com o resultado da requisição
 * @throws APIError se todas as tentativas falharem
 *
 * @example
 * ```typescript
 * const data = await apiWithRetry('/tools/agrupar-objetos-sobrepostos', {
 *   method: 'GET'
 * });
 * ```
 */
export async function apiWithRetry<T>(
	endpoint: string,
	options: RequestInit = {},
	config?: ApiRetryConfig,
	override: ApiOverrideOptions = {},
): Promise<T> {
	const finalConfig = {
		...DEFAULT_CONFIG,
		...config,
	};

	let lastError: APIError | null = null;

	for (let attempt = 0; attempt < finalConfig.maxRetries; attempt++) {
		try {
			// Primeira tentativa
			if (attempt === 0) {
				sendToTerminal(
					"📤 [API_RETRY] Primeira tentativa",
					{
						endpoint,
						method: options.method || "GET",
					},
					LogCategory.API,
				);

				return await api<T>(endpoint, options, override);
			}

			// Retry: calcular delay
			const delayMs = calculateBackoffDelay(
				attempt - 1, // -1 porque attempt começa em 0
				finalConfig.backoffBase,
				finalConfig.backoffMultiplier,
			);

			sendToTerminal(
				"🔄 [API_RETRY] Tentando novamente",
				{
					endpoint,
					attempt: attempt + 1,
					maxRetries: finalConfig.maxRetries,
					delayMs,
					lastError: lastError
						? {
								status: lastError.status,
								message: lastError.message,
							}
						: null,
				},
				LogCategory.API,
			);

			// Chamar callback se fornecido
			if (lastError && config?.onRetry) {
				config.onRetry(attempt, lastError, delayMs);
			}

			// Aguardar antes de retry
			await new Promise((resolve) => setTimeout(resolve, delayMs));

			// Tentar novamente
			return await api<T>(endpoint, options, override);
		} catch (error) {
			// ✅ CORRIGIDO: Converter erros não-APIError para APIError quando possível
			if (error instanceof APIError) {
				lastError = error;
			} else if (error instanceof Error) {
				// Tentar extrair status de erros de fetch/network
				let status = (error as any)?.status;

				// Se não tem status, tentar inferir da mensagem
				if (!status || status === 0) {
					if (
						error.message.includes("timeout") ||
						error.message.includes("tempo")
					) {
						status = 504;
					} else if (
						error.message.includes("network") ||
						error.message.includes("fetch") ||
						error.message.includes("Failed to fetch")
					) {
						status = 502;
					}
				}

				// Criar APIError genérico para erros de rede retryable
				if (status === 502 || status === 504) {
					lastError = new APIError(
						status,
						error.message,
						status === 504 ? "timeout" : "offline",
						{ originalError: error.message },
					);
				} else {
					// Erro não retryable - re-lançar imediatamente
					throw error;
				}
			} else {
				// Erro desconhecido - re-lançar imediatamente
				throw error;
			}

			// Verificar se é retryable
			const isRetryable =
				finalConfig.retryableStatuses.includes(lastError.status) ||
				isRetryableError(lastError);

			// Se não é retryable ou esgotou tentativas, lançar erro
			if (!isRetryable || attempt === finalConfig.maxRetries - 1) {
				if (!isRetryable) {
					sendToTerminal(
						"❌ [API_RETRY] Erro não retryable",
						{
							endpoint,
							status: lastError.status,
							message: lastError.message,
						},
						LogCategory.ERROR,
					);
				} else {
					sendToTerminal(
						"❌ [API_RETRY] Esgotadas todas as tentativas",
						{
							endpoint,
							attempts: finalConfig.maxRetries,
							lastError: {
								status: lastError.status,
								message: lastError.message,
							},
						},
						LogCategory.ERROR,
					);
				}

				throw lastError;
			}

			// Continuar para próxima tentativa
		}
	}

	// Nunca deve chegar aqui, mas TypeScript exige
	throw lastError || new APIError(500, "Erro desconhecido no retry", "unknown");
}
